import logger from '@core/util/universal-logger';
import { SET_KILLSWITCHES } from '@common-vue/bag-checkout-common-consumable/src/types/mutations';
import getQueue from '@utils/getQueue';
import { PAGE_DATA_REVALIDATED, SET_INITIAL_PAGE_DATA, SET_INITIAL_PAGE_ERROR } from '@types/actions';

let eventTarget;

try {
  eventTarget = new EventTarget();
} catch (_) {
  // Safari 13 does not support EventTarget constructor
  // as a fallback, we can trigger the events on the document
  eventTarget = document;
}

export default function useCacheUpdates(store, pageName, initialDataUrl) {
  const CACHE_UPDATED_OR_REVALIDATED = 'cacheUpdatedOrRevalidated';
  const { enqueue } = getQueue((error) => {
    logger.error('Error while processing the queue', error);
    // TODO: HIGH show Technical Glitch error overlay
  });
  const { origin, href } = window.location;
  const { pathname: initialDataUrlPathname } = new URL(initialDataUrl, href);
  const fullInitialDataUrl = origin + initialDataUrlPathname;
  // keys are the urls (e.g. https://local.mcom-119.tbe.zeus.fds.com/xapi/bag/v1/mybag ), values are methods to handle the data
  const dataHandlers = {
    // For now we are interested in IntialData updates only
    // In future, we might want to handle other API calls as well
    [fullInitialDataUrl]: (data, revalidationResult /* {success: Boolean, hasUpdates: undefined|true|false} */) => {
      if (revalidationResult === undefined) {
        // This is the CACHE_UPDATED scenario
        store.dispatch(SET_INITIAL_PAGE_DATA, { pageName, data });
      } else if (revalidationResult.success === true) {
        if (revalidationResult.hasUpdates === true) {
          // This is the CACHE_REVALIDATED scenario with updates
          store.dispatch(SET_INITIAL_PAGE_DATA, { pageName, data });
        } else {
          /*
            When HTML is rendered from cache, then the SEED cookie is not updated. SEED cookie is updated after /my/bag revalidation
            call has finished. Hence when /mybag renders from cache, then SEED cookie value is still old. When /mybag revalidation call
            finishes, then we need to update the KS based on experimentation using the new updated SEED cookie value.

            Before calling 'SET_KILLSWITCHES' here, the old values of Killswitches are already in the state from the cached
            /mybag response. After this code is executed, the old KS object gets merged with the new KS object (defaults to {})
            and update the Killswitches again based on SEED cookie.
          */
          logger.warn(' Updating the KS with no value when revalidation is successful with no updates');
          store.commit(SET_KILLSWITCHES);
        }
        // we are done with the revalidation of the initialDataUrl
        store.dispatch(PAGE_DATA_REVALIDATED, revalidationResult);
      } else if (revalidationResult.success === false) {
        // revalidation failed with some fetch call failure
        store.dispatch(SET_INITIAL_PAGE_ERROR, { pageName });
        store.dispatch(PAGE_DATA_REVALIDATED, revalidationResult);
      }
    },
  };

  async function getCachedData(cacheName, url) {
    const cache = await caches.open(cacheName);
    const response = await cache.match(url);
    // TODO: MEDIUM For some scenarios, the response might not be a json
    const data = await response.json();

    return data;
  }

  async function processUpdatedData({
    payload: {
      cacheName, updatedURL, success, hasUpdates, redirectUrl,
    }, type,
  }) {
    if (updatedURL in dataHandlers) {
      try {
        let updatedData;
        let revalidationResult;

        if (type === 'CACHE_REVALIDATED') {
          revalidationResult = { success, hasUpdates };
          if (success === true) {
            if (hasUpdates) { // APIRevalidationtUpdatePlugin.runRevalidationChecks reported that the revalidation has new updates
              /**
               Assumption: Any route that uses macysSW.APIRevalidationtUpdatePlugin
              will NOT be using the BroadcastUpdatePlugin plugin. See details in the CACHE_UPDATED section below:

              APIRevalidationtUpdatePlugin.runRevalidationChecks is called by RecentStaleOrStaleWhileRevalidate.
              When RecentStaleOrStaleWhileRevalidate compares the responses,
              it compares the cached response, that was there when revalidation call started
              (this is different from workbox.strategies.StrategyHandler's cachePut method. See details in the CACHE_UPDATED section below: )
              As a result this does not suffer from the race condition that BroadcastUpdatePlugin has.
              */

              // revalidation response had updated response. Let's pull the updated response from the cache
              updatedData = await getCachedData(cacheName, updatedURL);
              logger.info(' [workbox] got the updated revalidated data for', updatedURL, updatedData);
            }

            logger.info(` [workbox] re-validated url successfully (hasUpdates: ${hasUpdates})`, updatedURL);
          } else if (success === false) {
            // 'redirectUrl' from the payload is the value from headers['Redirect_url'] of revalidated response.
            if (redirectUrl) {
              logger.error(' [workbox] re-validation of url failed, with a redirect url', redirectUrl);
              window.location.assign(redirectUrl);

              // return undefined to prevent any action on bag before redirect finishes.
              return;
            }
            logger.error(' [workbox] re-validation of url failed', updatedURL);
          }
        } else if (type === 'CACHE_UPDATED') {
          /**
           Any route that uses macysSW.APIRevalidationtUpdatePlugin, should NOT use BroadcastUpdatePlugin plugin. See details below:

            workbox.strategies.StrategyHandler's cachePut method compares the cache's response with the new response
            When we are revalidating a response, if some other call updates the cache with the same response, then
            BroadcastCacheUpdate.notifyIfUpdated method will not recognize that the cache has been updated by the revalidation request
            Hence CACHE_UPDATED event will not fire

            For ATB scenario, we make a prerender call to cache the GetBag call
            If user goes to the bag page right away, then both the /mybag?prerender=true and bag's /mybag call run in parallel.
            Here we have a race condition. As a result, we run in the above scenario where /mybag?prerender call upadtes the cache before
            Bag's /mybag revalidation call has a chance to update the cache
          */

          updatedData = await getCachedData(cacheName, updatedURL);
          logger.info(' [workbox] got the updated data for', updatedURL, updatedData);
        }

        dataHandlers[updatedURL](updatedData, revalidationResult);
      } catch (e) {
        logger.error(' [workbox] unable to process the updated data for', updatedURL, e);
      }
    }
    /*
    The following condition will always be false
    This is handled from the index.template.html file
    else if (updatedURL === origin + window.location.pathname) {
      // if the current page's HTML has changed, then reload it
      logger.info('workbox:::::::::::::::Got the updated data for', updatedURL, 'reloading the page....');
      window.location.reload();
    }
    */
  }

  const { updates } = window.CacheUpdateHandler;
  let updateIndex = 0;
  // process any pending updates here
  while (updateIndex < updates.length) {
    const update = updates[updateIndex];
    enqueue(async () => processUpdatedData(update));
    updateIndex += 1;
  }

  // update the CacheHandler
  // so that it could start firing the CACHE_UPDATED_OR_REVALIDATED event
  eventTarget.addEventListener(CACHE_UPDATED_OR_REVALIDATED, (e) => {
    enqueue(async () => processUpdatedData(e.detail));
  });
  window.CacheUpdateHandler = {
    notify(data /* {payload, type} */) {
      eventTarget.dispatchEvent(new CustomEvent(CACHE_UPDATED_OR_REVALIDATED, { detail: data }));
    },
  };
}
